import { Table, message } from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ExcelDownloadModal from "../../../Pages/Masters/Common/excelDownloadAccess";
import apiCall from "../../../components/Api";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import Pagination from "../../../components/Common/CommonPagination/index";
import MainconternLayout from "../../../components/Layout/MainconternLayout";
import AddButton from "../../../components/Common/addButton";
import FilterOptions from "../../../components/Common/antdFilterOptions";
import UploadExcel from "../../../components/Common/uploadExcel";
import { prescriptionType, returnable } from "../../../components/Constants";
import Deletepopup from "../../../components/Modal/AntdDeletepopup";
import { MedicineDetailModal } from "./MedicineDetailModal";
import scroll from "../../../assets/webp/scroll.webp";
import {
  NewFilterModal,
  breadcrumbsList,
  downloadMedicineList,
  fetchManufacturerList,
  fetchMedicineList,
  fetchMedicineTypes,
  fetchSaltList,
  getMedicineTableColumns,
} from "./utils";

const MedicineDetails = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState("newest");
  const [records, setRecords] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ id: "", status: false });
  const [searchText, setSearchText] = useState("");

  const [visibleFilter, setVisibleFilter] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [medicineTypes, setMedicineTypes] = useState([]);
  const [saltList, setSaltList] = useState([]);
  const [manufacturerList, setManufacturerList] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({});
  const [total_records, setTotalRecords] = useState(0);
  const [showMedicineDetails, setshowMedicineDetails] = useState(false);
  const [loader, setLoader] = useState(true);
  const [MedicineDetails, setMedicineDetails] = useState();

  let discounts = [];
  for (let i = 5; i <= 80; i += 5) {
    discounts.push(i + "%");
  }

  const filterOptions = useMemo(
    () => ({
      manufacturer_name: manufacturerList,
      molecule_name: saltList,
      product_type_name: medicineTypes,
      discount: discounts,
      prescription_type: ["Rx", "Non-Rx"],
      medicine_status: ["Active", "Non-Active"],
    }),
    [manufacturerList, saltList, medicineTypes],
  );

  const list_api_call = useCallback(
    async (filterValues) => {
      try {
        const data = await fetchMedicineList({
          skip,
          limit,
          sort,
          searchText,
          filterValues,
        });
        setRecords(data.rows);
        setTotalRecords(data.count);
        setPages(Math.ceil(data.count / limit));
      } catch (error) {
        alert("Some error");
      }
    },
    [limit, searchText, skip, sort],
  );

  useEffect(() => {
    list_api_call();
  }, [limit, sort, skip, searchText, list_api_call]);

  useEffect(() => {
    (async () => {
      try {
        const [manufacturers, types, salts] = await Promise.all([
          fetchManufacturerList(),
          fetchMedicineTypes(),
          fetchSaltList(),
        ]);
        setManufacturerList(manufacturers);
        setMedicineTypes(types);
        setSaltList(salts);
      } catch (err) {
        alert(err.message);
      }
    })();
  }, []);

  const handlePageChange = (event) => {
    setSkip(event.selected * limit);
  };

  const handleActive = async (id, status) => {
    try {
      await apiCall(`medicine/update/${id}`, "PATCH", {
        medicine_url_status: !status,
      });

      list_api_call();
    } catch (err) {
      console.error(err);
    }
  };

  const debouncedSearch = debounce((value) => {
    handlePageChange({ selected: 0 });
    setSearchText(value);
  }, 500);

  const handleDelete = async (id) => {
    try {
      const res = await apiCall(`medicine/delete/${id}`, "DELETE", null, null);
      list_api_call();
      setDeleteModal({ id: "", status: false });
      message.success(`${res.data?.data?.medicine_name} deleted successfully`);
    } catch (error) {
      setDeleteModal({ id: "", status: false });
      message.error("Failed to delete item");
    }
  };

  const onApplyFilters = (filterValues) => {
    list_api_call(filterValues);
    setAppliedFilter(filterValues);
  };

  const onChangeSearch = (value) => debouncedSearch(value);

  const handleSortClick = (selectedSort) => setSort(selectedSort);

  const showModal = () => setIsModalOpen(true);

  const handleProductTypeForm = () =>
    navigate("/healthsy-services/order-medicines/medicine-details-form");

  const handleEdit = (id) =>
    navigate(`/healthsy-services/order-medicines/edit-medicine/${id}`);

  const handleClose = () => setDeleteModal({ id: "", status: false });

  const onClose = () => setVisibleFilter(false);

  const columns = getMedicineTableColumns({
    skip,
    showPopup,
    setShowPopup,
    handleEdit,
    setDeleteModal,
    handleActive,
    setshowMedicineDetails,
    setMedicineDetails,
  });

  const onClickDownload = async () => {
    try {
      await downloadMedicineList({
        appliedFilter,
        sort,
        searchText,
        prescriptionType,
        returnable,
      });
    } catch (err) {
      alert(err?.message || "An error occurred while downloading the file.");
    }
  };

  const filterKeyValues = {
    manufacturer_name: "Manufacturer",
    molecule_name: "Salt",
    product_type_name: "Medicine Type",
    variant: "Variant",
    discount: "Discount",
    prescription_type: "Prescription Type",
    medicine_status: "Status",
  };

  useEffect(() => {
    records.length > 0 && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <>
      <MainconternLayout
        header={
          <div className="d-flex pt-1 pb-2 flex-wrap align-items-center justify-content-between">
            <Breadcrumbs
              className="healthsyMasterBreadCrumbs pb-0"
              breadcrumbsList={breadcrumbsList}
            />
            <div className="d-flex gap-4 align-items-center">
              <div className="bgPrimary addBtn">
                <AddButton
                  onClick={handleProductTypeForm}
                  btnContent="Add New Medicine"
                  addBtn={true}
                  btnClassName="medicineManufacturer"
                />
              </div>
              <UploadExcel
                apiUrl="medicine/create-bulk"
                list_api_call={list_api_call}
                medCount={total_records}
              />
            </div>
          </div>
        }
        footer={
          <div className="mt-4 col d-flex align-items-center pagination justify-content-between admin-pagination">
            <div className="border border-danger bg-bg-light px-2 py-1 rounded">
              {" "}
              Total Medicines: {total_records}
            </div>
            {total_records >= limit && (
              <Pagination
                skip={skip}
                limit={limit}
                total={pages * limit}
                onPageChange={(selected) => handlePageChange({ selected })}
              />
            )}
          </div>
        }
      >
        <div className="healtharticle-container healthsyContainer">
          <FilterOptions
            limit={limit}
            handleSortClick={handleSortClick}
            setLimit={setLimit}
            onChangeSearch={onChangeSearch}
            setFilterShow={setVisibleFilter}
            downloadExcel={true}
            onClickDownload={showModal}
            isMed={true}
          />
          {!loader ? (
            <Table
              columns={columns}
              dataSource={records}
              pagination={false}
              rowKey="_id"
              className="mx-3 pt-2 table-scrollable medicineList_table"
              scroll={{ x: "max-content" }}
            />
          ) : (
            <div className="d-flex justify-content-center">
              <img src={scroll} width={150} alt="loader" />
            </div>
          )}
        </div>
      </MainconternLayout>

      {deleteModal.status === true && (
        <Deletepopup
          handleDelete={handleDelete}
          id={deleteModal?.id}
          isCloseModalEnable={true}
          handleClose={handleClose}
        />
      )}
      <ExcelDownloadModal
        onClickDownload={onClickDownload}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      {visibleFilter ? (
        <NewFilterModal
          visible={visibleFilter}
          onClose={onClose}
          filterOptions={filterOptions}
          onApplyFilters={onApplyFilters}
          appliedFilter={appliedFilter}
          defaultValue={"manufacturer_name"}
          filterKeyValues={filterKeyValues}
        />
      ) : null}
      {showMedicineDetails && (
        <MedicineDetailModal
          visible={showMedicineDetails}
          onClose={() => setshowMedicineDetails(false)}
          medicine={MedicineDetails}
        />
      )}
    </>
  );
};

export default MedicineDetails;
